import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { 
  ArrowLeft,
  Plus,
  Search,
  Loader2,
  ChevronRight,
  FileText,
  Send,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  ArrowRightCircle,
  Clock,
  DollarSign,
  Filter
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-zinc-500', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-500', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-orange-500', icon: AlertCircle },
  converted: { label: 'Converted', color: 'bg-[#ffd108] text-black', icon: ArrowRightCircle }
};

const MobileQuotes = () => {
  const navigate = useNavigate();
  const { api } = useMobileAuth();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved_value: 0
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const [quotesRes, customersRes] = await Promise.all([
        api.get('/api/quotes'),
        api.get('/api/customers')
      ]);
      
      setQuotes(quotesRes.data || []);
      setCustomers(customersRes.data || []);
      
      const quotesData = quotesRes.data || [];
      const pending = quotesData.filter(q => ['sent', 'viewed'].includes(q.status)).length;
      const approvedValue = quotesData
        .filter(q => q.status === 'approved')
        .reduce((sum, q) => sum + (q.total || 0), 0);
      
      setStats({
        total: quotesData.length,
        pending,
        approved_value: approvedValue
      });
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    return customer?.company_name || 'Unknown';
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-[var(--m-text)] text-xs flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = searchTerm === '' || 
      quote.quote_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCustomerName(quote.customer_id).toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr;
    }
  };

  return (
    <MobileLayout>
    <div className="min-h-full bg-[var(--m-bg)]">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center gap-3 mb-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/mobile')}
            className="text-[var(--m-text)]"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-[var(--m-text)] font-bold text-xl flex-1">Quotes</h1>
          <Button
            size="sm"
            onClick={() => navigate('/mobile/quotes/new')}
            className="bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
            data-testid="create-quote-btn"
          >
            <Plus className="h-4 w-4 mr-1" />
            New
          </Button>
        </div>

        {/* Search Bar */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--m-text-secondary)]" />
            <Input
              placeholder="Search quotes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-[var(--m-bg-elevated)] border-[var(--m-border-light)] text-[var(--m-text)] placeholder:text-[var(--m-text-muted)]"
            />
          </div>
          <Button
            variant="outline"
            size="icon"
            onClick={() => setShowFilters(!showFilters)}
            className={`border-[var(--m-border-light)] ${showFilters ? 'bg-[#ffd108] text-[#1a1a1a]' : 'text-[var(--m-text-secondary)]'}`}
          >
            <Filter className="h-4 w-4" />
          </Button>
        </div>

        {/* Filter Pills */}
        {showFilters && (
          <div className="flex flex-wrap gap-2 mt-3">
            {['all', 'draft', 'sent', 'viewed', 'approved', 'rejected', 'converted'].map((status) => (
              <Badge
                key={status}
                onClick={() => setStatusFilter(status)}
                className={`cursor-pointer ${
                  statusFilter === status 
                    ? 'bg-[#ffd108] text-[#1a1a1a]' 
                    : 'bg-[var(--m-bg-elevated)] text-[var(--m-text-body)] hover:bg-[var(--m-hover)]'
                }`}
              >
                {status === 'all' ? 'All' : STATUS_CONFIG[status]?.label || status}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="p-4 space-y-4 pb-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardContent className="p-3 text-center">
              <FileText className="h-5 w-5 text-[#ffd108] mx-auto mb-1" />
              <p className="text-xl font-bold text-[var(--m-text)]">{stats.total}</p>
              <p className="text-xs text-[var(--m-text-secondary)]">Total</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardContent className="p-3 text-center">
              <Clock className="h-5 w-5 text-amber-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-[var(--m-text)]">{stats.pending}</p>
              <p className="text-xs text-[var(--m-text-secondary)]">Pending</p>
            </CardContent>
          </Card>
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardContent className="p-3 text-center">
              <DollarSign className="h-5 w-5 text-green-400 mx-auto mb-1" />
              <p className="text-lg font-bold text-[var(--m-text)]">${(stats.approved_value / 1000).toFixed(1)}k</p>
              <p className="text-xs text-[var(--m-text-secondary)]">Approved</p>
            </CardContent>
          </Card>
        </div>

        {/* Quotes List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </div>
        ) : filteredQuotes.length === 0 ? (
          <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-[var(--m-text-secondary)] mb-4">
                {searchTerm || statusFilter !== 'all' 
                  ? 'No quotes match your search' 
                  : 'No quotes yet'}
              </p>
              <Button
                onClick={() => navigate('/mobile/quotes/new')}
                className="bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First Quote
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {filteredQuotes.map((quote) => (
              <Card 
                key={quote.id}
                className="bg-[var(--m-bg-card)] border-[var(--m-border)] cursor-pointer hover:border-[#ffd108] transition-colors"
                onClick={() => navigate(`/mobile/quotes/${quote.id}`)}
                data-testid={`quote-card-${quote.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[#ffd108] font-bold text-sm">
                          {quote.quote_number}
                        </span>
                        {getStatusBadge(quote.status)}
                      </div>
                      <h3 className="text-[var(--m-text)] font-medium truncate">
                        {getCustomerName(quote.customer_id)}
                      </h3>
                      <div className="flex items-center gap-3 mt-2 text-[var(--m-text-secondary)] text-sm">
                        <span>{quote.currency_symbol || '$'}{quote.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        <span>•</span>
                        <span>{formatDate(quote.created_at)}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-[var(--m-text-muted)] flex-shrink-0 mt-1" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Floating Action Button */}
      <Button
        onClick={() => navigate('/mobile/quotes/new')}
        className="fixed bottom-[calc(84px+env(safe-area-inset-bottom,0px))] right-4 h-14 w-14 rounded-full bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90 shadow-lg z-10"
        data-testid="fab-create-quote"
      >
        <Plus className="h-6 w-6" />
      </Button>
    </div>
    </MobileLayout>
  );
};

export default MobileQuotes;
